import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import BatchForm from '@/components/BatchForm';
import { batchApi } from '@/lib/api';
import { Batch } from '@/types';

const EditBatchPage = () => {
  const { id } = useParams<{ id: string }>();
  const [batch, setBatch] = useState<Batch | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchBatch = async () => {
      if (!id) {
        setError('No batch ID provided');
        setLoading(false);
        return;
      }

      try {
        const batchData = await batchApi.getById(id);
        setBatch(batchData);
      } catch (err) {
        console.error('Error fetching batch:', err);
        setError('Failed to load batch data');
      } finally {
        setLoading(false);
      }
    };

    fetchBatch();
  }, [id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-6 w-6 border-2 border-gray-200 border-t-blue-600 mx-auto mb-3"></div>
          <p className="text-muted-foreground text-sm">Loading batch data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-4">
          <Button variant="outline" size="sm" asChild>
            <Link to="/settings/batches">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Batches
            </Link>
          </Button>
          <h1 className="text-3xl font-bold tracking-tight">Edit Batch</h1>
        </div>
        <Card>
          <CardContent className="pt-6">
            <p className="text-destructive">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-4">
        <Button variant="outline" size="sm" asChild>
          <Link to="/settings/batches">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Batches
          </Link>
        </Button>
        <h1 className="text-3xl font-bold tracking-tight">
          Edit Batch: {batch?.year}
        </h1>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Edit Batch Details</CardTitle>
        </CardHeader>
        <CardContent>
          {batch && <BatchForm batch={batch} isEditing={true} />}
        </CardContent>
      </Card>
    </div>
  );
};

export default EditBatchPage;
